import React, { useEffect, useState, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Tag } from 'primereact/tag';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import { Toast } from 'primereact/toast';
import StreamBuilder from '../../components/common/StreamBuilder';
import { BlocManagementNotifications, NotificationViewState } from '../../bloc/BlocManagementNotifications';
import { Notification } from '../../../types/cms.types';
import { CpnCreateEditNotification } from '../../components/notifications/CpnCreateEditNotification';
import { formatDistance } from 'date-fns';
import { vi } from 'date-fns/locale';
import AdminProtectedRoute from '../../components/admin/AdminProtectedRoute';
import AdminLayout from '../../components/admin/AdminLayout';

export const NotificationsManagementPage: React.FC = () => {
    const [bloc] = useState(() => new BlocManagementNotifications());
    const toast = useRef<Toast>(null);
    const [showDialog, setShowDialog] = useState(false);
    const [editingNotification, setEditingNotification] = useState<Notification | null>(null);
    const [lazyLoading, setLazyLoading] = useState(false);

    // Filter states
    const [selectedTopic, setSelectedTopic] = useState<string | null>(null);
    const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
    const [selectedSendType, setSelectedSendType] = useState<string | null>(null);

    useEffect(() => {
        bloc.initData();
    }, [bloc]);

    // Status severity mapping
    const getStatusSeverity = (status: string): 'success' | 'info' | 'warning' | 'danger' => {
        switch (status) {
            case 'sent': return 'success';
            case 'pending': return 'info';
            case 'cancelled': return 'warning';
            case 'failed': return 'danger';
            default: return 'info';
        }
    };

    // Status label mapping
    const getStatusLabel = (status: string): string => {
        switch (status) {
            case 'sent': return 'Đã gửi';
            case 'pending': return 'Chờ gửi';
            case 'cancelled': return 'Đã hủy';
            case 'failed': return 'Thất bại';
            default: return status;
        }
    };

    // Topic label mapping
    const getTopicLabel = (topic: string): string => {
        switch (topic) {
            case 'app_user': return 'Khách hàng';
            case 'app_driver': return 'Tài xế';
            default: return topic;
        }
    };

    // Send type label mapping
    const getSendTypeLabel = (sendType: string): string => {
        switch (sendType) {
            case 'immediate': return 'Ngay lập tức';
            case 'scheduled': return 'Đặt lịch';
            default: return sendType;
        }
    };

    // Filter handlers
    const handleTopicFilter = async (topic: string) => {
        setSelectedTopic(topic);
        await bloc.filterByTopic(topic);
    };

    const handleStatusFilter = async (status: string) => {
        setSelectedStatus(status);
        await bloc.filterByStatus(status);
    };

    const handleSendTypeFilter = async (sendType: string) => {
        setSelectedSendType(sendType);
        await bloc.filterBySendType(sendType);
    };

    // Clear filters
    const clearFilters = async () => {
        setSelectedTopic(null);
        setSelectedStatus(null);
        setSelectedSendType(null);
        await bloc.clearFilters();
    };

    // Refresh handler
    const handleRefresh = async () => {
        await bloc.loadNotifications();
        await bloc.loadStatistics();
    };

    // Page change handler
    const onPageChange = async (event: any) => {
        setLazyLoading(true);

        if (event.rows !== bloc.currentPagination.limit) {
            await bloc.changePageSize(event.rows);
        } else {
            await bloc.changePage(event.page + 1);
        }

        setLazyLoading(false);
    };

    // Action handlers
    const handleCreate = () => {
        setEditingNotification(null);
        setShowDialog(true);
    };

    const handleEdit = (notification: Notification) => {
        setEditingNotification(notification);
        setShowDialog(true);
    };

    const handleSendNow = (notification: Notification) => {
        confirmDialog({
            message: `Bạn có chắc muốn gửi ngay thông báo "${notification.title}"?`,
            header: 'Xác nhận gửi',
            icon: 'pi pi-send',
            acceptLabel: 'Gửi ngay',
            rejectLabel: 'Hủy',
            accept: async () => {
                try {
                    await bloc.sendNotificationNow(notification.id);
                    toast.current?.show({
                        severity: 'success',
                        summary: 'Thành công',
                        detail: 'Đã gửi thông báo',
                        life: 3000
                    });
                } catch (error: any) {
                    toast.current?.show({
                        severity: 'error',
                        summary: 'Lỗi',
                        detail: error.message,
                        life: 5000
                    });
                }
            }
        });
    };

    const handleResend = (notification: Notification) => {
        confirmDialog({
            message: `Bạn có chắc muốn gửi lại thông báo "${notification.title}"?`,
            header: 'Xác nhận gửi lại',
            icon: 'pi pi-refresh',
            acceptLabel: 'Gửi lại',
            rejectLabel: 'Hủy',
            accept: async () => {
                try {
                    await bloc.resendNotification(notification.id);
                    toast.current?.show({
                        severity: 'success',
                        summary: 'Thành công',
                        detail: 'Đã gửi lại thông báo',
                        life: 3000
                    });
                } catch (error: any) {
                    toast.current?.show({
                        severity: 'error',
                        summary: 'Lỗi',
                        detail: error.message,
                        life: 5000
                    });
                }
            }
        });
    };

    const handleCancel = (notification: Notification) => {
        confirmDialog({
            message: `Bạn có chắc muốn hủy thông báo "${notification.title}"?`,
            header: 'Xác nhận hủy',
            icon: 'pi pi-ban',
            acceptLabel: 'Hủy thông báo',
            rejectLabel: 'Đóng',
            acceptClassName: 'p-button-warning',
            accept: async () => {
                try {
                    await bloc.cancelNotification(notification.id);
                    toast.current?.show({
                        severity: 'success',
                        summary: 'Thành công',
                        detail: 'Đã hủy thông báo',
                        life: 3000
                    });
                } catch (error: any) {
                    toast.current?.show({
                        severity: 'error',
                        summary: 'Lỗi',
                        detail: error.message,
                        life: 5000
                    });
                }
            }
        });
    };

    const handleDelete = (notification: Notification) => {
        confirmDialog({
            message: `Bạn có chắc muốn xóa thông báo "${notification.title}"?`,
            header: 'Xác nhận xóa',
            icon: 'pi pi-trash',
            acceptLabel: 'Xóa',
            rejectLabel: 'Hủy',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    await bloc.deleteNotification(notification.id);
                    toast.current?.show({
                        severity: 'success',
                        summary: 'Thành công',
                        detail: 'Đã xóa thông báo',
                        life: 3000
                    });
                } catch (error: any) {
                    toast.current?.show({
                        severity: 'error',
                        summary: 'Lỗi',
                        detail: error.message,
                        life: 5000
                    });
                }
            }
        });
    };

    const handleDialogSave = async () => {
        setShowDialog(false);
        setEditingNotification(null);
        await bloc.loadNotifications();
        toast.current?.show({
            severity: 'success',
            summary: 'Thành công',
            detail: editingNotification ? 'Đã cập nhật thông báo' : 'Đã tạo thông báo',
            life: 3000
        });
    };

    // Filter options
    const topicOptions = [
        { label: 'Tất cả', value: null },
        { label: '👤 Khách hàng', value: 'app_user' },
        { label: '🚗 Tài xế', value: 'app_driver' }
    ];

    const statusOptions = [
        { label: 'Tất cả', value: null },
        { label: 'Chờ gửi', value: 'pending' },
        { label: 'Đã gửi', value: 'sent' },
        { label: 'Thất bại', value: 'failed' },
        { label: 'Đã hủy', value: 'cancelled' }
    ];

    const sendTypeOptions = [
        { label: 'Tất cả', value: null },
        { label: 'Ngay lập tức', value: 'immediate' },
        { label: 'Đặt lịch', value: 'scheduled' }
    ];

    // Column templates
    const titleBodyTemplate = (rowData: Notification) => (
        <div>
            <div style={{ fontWeight: '600', color: '#3b82f6', marginBottom: '4px' }}>
                {rowData.title}
            </div>
            <div style={{ fontSize: '0.875rem', color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                {rowData.body}
            </div>
        </div>
    );

    const topicTemplate = (rowData: Notification) => (
        <Tag
            value={getTopicLabel(rowData.topic)}
            severity={rowData.topic === 'app_user' ? 'info' : 'warning'}
            icon={rowData.topic === 'app_user' ? 'pi pi-user' : 'pi pi-car'}
        />
    );

    const sendTypeTemplate = (rowData: Notification) => (
        <Tag
            value={getSendTypeLabel(rowData.send_type)}
            severity={rowData.send_type === 'immediate' ? 'success' : 'info'}
            icon={rowData.send_type === 'immediate' ? 'pi pi-send' : 'pi pi-clock'}
        />
    );

    const scheduledAtTemplate = (rowData: Notification) => {
        if (!rowData.scheduled_at) return '-';

        const date = new Date(rowData.scheduled_at);
        const now = new Date();
        const isPast = date < now;

        return (
            <div style={{ color: isPast ? '#6b7280' : '#3b82f6', fontWeight: isPast ? 'normal' : '600' }}>
                <div>{date.toLocaleDateString('vi-VN')}</div>
                <div style={{ fontSize: '0.75rem' }}>{date.toLocaleTimeString('vi-VN')}</div>
                <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '4px' }}>
                    {formatDistance(date, now, { addSuffix: true, locale: vi })}
                </div>
            </div>
        );
    };

    const statusTemplate = (rowData: Notification) => (
        <div>
            <Tag
                value={getStatusLabel(rowData.status)}
                severity={getStatusSeverity(rowData.status)}
            />
            {rowData.sent_at && (
                <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '4px' }}>
                    {formatDistance(new Date(rowData.sent_at), new Date(), {
                        addSuffix: true,
                        locale: vi
                    })}
                </div>
            )}
            {rowData.error_message && (
                <div style={{ fontSize: '0.75rem', color: '#ef4444', marginTop: '4px' }} title={rowData.error_message}>
                    <i className="pi pi-exclamation-triangle" style={{ marginRight: '4px' }}></i>
                    {rowData.error_message.substring(0, 30)}...
                </div>
            )}
        </div>
    );

    const actionsTemplate = (rowData: Notification) => (
        <div style={{ display: 'flex', gap: '8px' }}>
            {rowData.status === 'pending' && (
                <Button
                    icon="pi pi-send"
                    className="p-button-text p-button-success"
                    tooltip="Gửi ngay"
                    onClick={() => handleSendNow(rowData)}
                />
            )}

            {rowData.status === 'failed' && (
                <Button
                    icon="pi pi-refresh"
                    className="p-button-text p-button-warning"
                    tooltip="Gửi lại"
                    onClick={() => handleResend(rowData)}
                />
            )}

            {rowData.status === 'pending' && rowData.send_type === 'scheduled' && (
                <Button
                    icon="pi pi-ban"
                    className="p-button-text p-button-warning"
                    tooltip="Hủy"
                    onClick={() => handleCancel(rowData)}
                />
            )}

            {rowData.status === 'pending' && (
                <Button
                    icon="pi pi-pencil"
                    className="p-button-text"
                    tooltip="Sửa"
                    onClick={() => handleEdit(rowData)}
                />
            )}

            <Button
                icon="pi pi-trash"
                className="p-button-text p-button-danger"
                tooltip="Xóa"
                onClick={() => handleDelete(rowData)}
            />
        </div>
    );

    return (
        <AdminProtectedRoute
            requiredModule="notification_management"
            requiredSubModule="notification_list"
            requiredPermission="view"
        >
            <AdminLayout>
                <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Toast ref={toast} />
                    <ConfirmDialog />

                    {/* Table Container */}
                    <div style={{ flex: 1, minHeight: 0, background: 'white' }}>
                        <StreamBuilder
                            stream={bloc.getStream('info')}
                            initialData={{
                                notifications: [],
                                pagination: { total: 0, page: 1, limit: 10, totalPages: 0 },
                                filters: {},
                                loading: false,
                                error: null,
                                hasData: false
                            }}
                            builder={(snapshot: any) => {
                                const state: NotificationViewState = snapshot.data;

                                if (state.error) {
                                    return (
                                        <div style={{ padding: '24px' }}>
                                            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', padding: '16px' }}>
                                                <div style={{ display: 'flex' }}>
                                                    <i className="pi pi-exclamation-triangle" style={{ color: '#f87171', marginRight: '8px' }}></i>
                                                    <div style={{ color: '#991b1b' }}>
                                                        <h3 style={{ fontSize: '0.875rem', fontWeight: '500' }}>Có lỗi xảy ra</h3>
                                                        <p style={{ fontSize: '0.875rem', marginTop: '4px' }}>{state.error}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                }

                                // Statistics cards
                                const statisticsSection = state.statistics && (
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                        gap: '16px',
                                        padding: '16px 24px',
                                        borderBottom: '1px solid #e5e7eb',
                                        background: '#f9fafb'
                                    }}>
                                        <div style={{ background: 'white', borderRadius: '8px', padding: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <div>
                                                    <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Tổng số</div>
                                                    <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#3b82f6' }}>
                                                        {state.statistics.total}
                                                    </div>
                                                </div>
                                                <i className="pi pi-bell" style={{ fontSize: '2rem', color: '#3b82f6', opacity: 0.2 }}></i>
                                            </div>
                                        </div>

                                        <div style={{ background: 'white', borderRadius: '8px', padding: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <div>
                                                    <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Chờ gửi</div>
                                                    <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#3b82f6' }}>
                                                        {state.statistics.pending}
                                                    </div>
                                                </div>
                                                <i className="pi pi-clock" style={{ fontSize: '2rem', color: '#3b82f6', opacity: 0.2 }}></i>
                                            </div>
                                        </div>

                                        <div style={{ background: 'white', borderRadius: '8px', padding: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <div>
                                                    <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Đã gửi</div>
                                                    <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#10b981' }}>
                                                        {state.statistics.sent}
                                                    </div>
                                                </div>
                                                <i className="pi pi-check-circle" style={{ fontSize: '2rem', color: '#10b981', opacity: 0.2 }}></i>
                                            </div>
                                        </div>

                                        <div style={{ background: 'white', borderRadius: '8px', padding: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <div>
                                                    <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Thất bại</div>
                                                    <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#ef4444' }}>
                                                        {state.statistics.failed}
                                                    </div>
                                                </div>
                                                <i className="pi pi-times-circle" style={{ fontSize: '2rem', color: '#ef4444', opacity: 0.2 }}></i>
                                            </div>
                                        </div>

                                        <div style={{ background: 'white', borderRadius: '8px', padding: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <div>
                                                    <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Đã hủy</div>
                                                    <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#f97316' }}>
                                                        {state.statistics.cancelled}
                                                    </div>
                                                </div>
                                                <i className="pi pi-ban" style={{ fontSize: '2rem', color: '#f97316', opacity: 0.2 }}></i>
                                            </div>
                                        </div>
                                    </div>
                                );

                                // Header template with filters
                                const headerTemplate = (
                                    <div style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: '16px',
                                        alignItems: 'center',
                                        padding: '16px 24px',
                                        borderBottom: '1px solid #e5e7eb',
                                        background: '#f9fafb'
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                                            <i className="pi pi-bell" style={{ fontSize: '1.5rem', color: '#3b82f6' }}></i>
                                            <h2 style={{ fontSize: '1.25rem', fontWeight: '700', margin: 0 }}>
                                                Quản lý thông báo
                                            </h2>
                                        </div>

                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <Button
                                                icon="pi pi-filter-slash"
                                                onClick={clearFilters}
                                                className="p-button-outlined p-button-secondary p-button-sm"
                                                tooltip="Xóa bộ lọc"
                                            />
                                            <Button
                                                icon="pi pi-refresh"
                                                onClick={handleRefresh}
                                                className="p-button-outlined p-button-sm"
                                                tooltip="Làm mới"
                                            />
                                            <Button
                                                label="Tạo thông báo"
                                                icon="pi pi-plus"
                                                onClick={handleCreate}
                                                className="p-button-success p-button-sm"
                                            />
                                        </div>
                                    </div>
                                );

                                return (
                                    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                        {statisticsSection}

                                        {/* Table */}
                                        <div style={{ flex: 1, overflow: 'hidden' }}>
                                            <DataTable
                                                scrollHeight="calc(100vh - 380px)"
                                                value={state.notifications}
                                                loading={state.loading || lazyLoading}
                                                loadingIcon="pi pi-spinner pi-spin"
                                                emptyMessage="Không có thông báo nào"
                                                responsiveLayout="scroll"
                                                scrollable
                                                size="small"
                                                stripedRows
                                                header={headerTemplate}
                                                showGridlines
                                                paginator={state.pagination.total > 0}
                                                rows={state.pagination.limit}
                                                totalRecords={state.pagination.total}
                                                first={(state.pagination.page - 1) * state.pagination.limit}
                                                onPage={onPageChange}
                                                rowsPerPageOptions={[10, 20, 50]}
                                                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
                                                currentPageReportTemplate="Hiển thị {first} đến {last} trong tổng số {totalRecords} bản ghi"
                                            >
                                                <Column
                                                    field="title"
                                                    header="Tiêu đề / Nội dung"
                                                    body={titleBodyTemplate}
                                                    style={{ minWidth: '300px' }}
                                                    frozen
                                                />
                                                <Column
                                                    field="topic"
                                                    header="Topic"
                                                    body={topicTemplate}
                                                    style={{ minWidth: '150px' }}
                                                    filter
                                                    showFilterMenuOptions={false}
                                                    showClearButton={false}
                                                    filterElement={
                                                        <Dropdown
                                                            value={selectedTopic}
                                                            options={topicOptions}
                                                            onChange={(e) => handleTopicFilter(e.value)}
                                                            placeholder="Tất cả topic"
                                                            style={{ width: '100%', fontSize: '0.75rem' }}
                                                            className="p-column-filter"
                                                        />
                                                    }
                                                />
                                                <Column
                                                    field="send_type"
                                                    header="Loại gửi"
                                                    body={sendTypeTemplate}
                                                    style={{ minWidth: '150px' }}
                                                    filter
                                                    showFilterMenuOptions={false}
                                                    showClearButton={false}
                                                    filterElement={
                                                        <Dropdown
                                                            value={selectedSendType}
                                                            options={sendTypeOptions}
                                                            onChange={(e) => handleSendTypeFilter(e.value)}
                                                            placeholder="Tất cả loại"
                                                            style={{ width: '100%', fontSize: '0.75rem' }}
                                                            className="p-column-filter"
                                                        />
                                                    }
                                                />
                                                <Column
                                                    field="scheduled_at"
                                                    header="Thời gian gửi"
                                                    body={scheduledAtTemplate}
                                                    style={{ minWidth: '180px' }}
                                                />
                                                <Column
                                                    field="status"
                                                    header="Trạng thái"
                                                    body={statusTemplate}
                                                    style={{ minWidth: '150px' }}
                                                    filter
                                                    showFilterMenuOptions={false}
                                                    showClearButton={false}
                                                    filterElement={
                                                        <Dropdown
                                                            value={selectedStatus}
                                                            options={statusOptions}
                                                            onChange={(e) => handleStatusFilter(e.value)}
                                                            placeholder="Tất cả trạng thái"
                                                            style={{ width: '100%', fontSize: '0.75rem' }}
                                                            className="p-column-filter"
                                                        />
                                                    }
                                                />
                                                <Column
                                                    header="Thao tác"
                                                    body={actionsTemplate}
                                                    style={{ minWidth: '180px' }}
                                                    frozen
                                                    alignFrozen="right"
                                                />
                                            </DataTable>
                                        </div>

                                        {/* Show message when no data and not loading */}
                                        {!state.loading && state.notifications.length === 0 && (
                                            <div style={{ flexShrink: 0, padding: '32px', textAlign: 'center' }}>
                                                <div style={{ color: '#6b7280' }}>
                                                    <i className="pi pi-info-circle" style={{ fontSize: '2rem', marginBottom: '12px', display: 'block' }}></i>
                                                    <p style={{ fontSize: '1.125rem', fontWeight: '500' }}>Chưa có dữ liệu thông báo</p>
                                                    <p style={{ fontSize: '0.875rem', marginTop: '4px' }}>Hệ thống chưa có thông báo nào được tạo.</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            }}
                        />
                    </div>
                </div>
            </AdminLayout>

            {/* Create/Edit Dialog */}
            <CpnCreateEditNotification
                visible={showDialog}
                notification={editingNotification}
                onHide={() => {
                    setShowDialog(false);
                    setEditingNotification(null);
                }}
                onSave={handleDialogSave}
            />
        </AdminProtectedRoute>
    );
};

export default NotificationsManagementPage;
